import { useCallback, useEffect, useState } from "react";
import { cn } from "./ui";
import { apiErrorText } from "@/lib/api";
import { onServerEvent } from "@/lib/events";
import { useAuth } from "./auth";
import { aed, zohoApi, type SalespersonRow, type SalespersonsStatus, type ZohoStatus } from "./companies-api";
import { locale, t } from "@/lib/i18n";

/* Состояние связи с Zoho и панель «Zoho CRM и Books → Hub»: синхронизация, история Wazzup и продавцы (Sales Person).
   Используется на главной в разделе «Zoho» (#zoho-link) и в окне «Настройки → Zoho». */

export const when = (iso: string | null) => (iso ? new Date(iso).toLocaleString(locale, { dateStyle: "short", timeStyle: "short" }) : "—");

const input =
  "h-9 w-full rounded-btn border border-border bg-background px-2.5 text-[13px] text-foreground outline-none transition focus:border-primary";

export function useZohoSync(flash: (text: string) => void) {
  const [st, setSt] = useState<ZohoStatus | null>(null);
  const [sp, setSp] = useState<SalespersonsStatus | null>(null);
  const [spBusy, setSpBusy] = useState("");

  const loadStatus = useCallback(
    () => Promise.all([zohoApi.status().then(setSt), zohoApi.salespersons().then(setSp, () => undefined)]).then(() => undefined, (e) => flash(apiErrorText(e))),
    [flash],
  );

  useEffect(() => {
    void loadStatus();
  }, [loadStatus]);
  // окончание синхронизации приходит уведомлением; пока идёт — опрос
  useEffect(() => onServerEvent("notification", () => void loadStatus()), [loadStatus]);
  useEffect(() => {
    if (!st?.running && !st?.wazzup?.running && !sp?.running) return;
    const timer = setInterval(() => void loadStatus(), 5000);
    return () => clearInterval(timer);
  }, [st?.running, st?.wazzup?.running, sp?.running, loadStatus]);

  const sync = async () => {
    try {
      await zohoApi.sync();
      flash(t("Синхронизация запущена — по окончании придёт уведомление"));
      void loadStatus();
    } catch (e) {
      flash(apiErrorText(e));
    }
  };

  const importWazzup = async (full: boolean) => {
    try {
      await zohoApi.wazzupImport(full);
      flash(t("Импорт истории Wazzup запущен — по окончании придёт уведомление"));
      void loadStatus();
    } catch (e) {
      flash(apiErrorText(e));
    }
  };

  const linkWazzup = async () => {
    try {
      await zohoApi.wazzupLink();
      flash(t("Привязка архива к компаниям запущена — диалоги появятся в карточках через несколько минут"));
    } catch (e) {
      flash(apiErrorText(e));
    }
  };

  const dedupeContacts = async () => {
    try {
      await zohoApi.dedupeContacts();
      flash(t("Слияние дублей запущено — по окончании придёт уведомление"));
    } catch (e) {
      flash(apiErrorText(e));
    }
  };

  const runSalespersons = async () => {
    try {
      await zohoApi.salespersonsRun("archive");
      flash(t("Распределение запущено — по окончании придёт уведомление"));
      void loadStatus();
    } catch (e) {
      flash(apiErrorText(e));
    }
  };

  const linkSalesperson = async (row: SalespersonRow, body: { userId?: string | null; createUser?: { email: string; role: string } }) => {
    setSpBusy(row.name);
    try {
      const r = await zohoApi.salespersonLink(row.name, body);
      flash(r.assigned ? t("{name}: назначено диалогов — {n}", { name: row.name, n: r.assigned }) : t("{name}: привязка сохранена", { name: row.name }));
      void loadStatus();
    } catch (e) {
      flash(apiErrorText(e));
    } finally {
      setSpBusy("");
    }
  };

  return { st, sp, spBusy, loadStatus, sync, importWazzup, linkWazzup, dedupeContacts, runSalespersons, linkSalesperson };
}

export type ZohoSync = ReturnType<typeof useZohoSync>;

/** Шапка «Zoho CRM и Books → Hub», четыре показателя, блок истории Wazzup и панель продавцов. */
export function ZohoSyncPanel({ z, hideHead }: { z: ZohoSync; hideHead?: boolean }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "owner" || user?.role === "admin";
  const [spEmail, setSpEmail] = useState<Record<string, string>>({});
  const { st, sp, spBusy } = z;
  const books = st?.sync.find((s) => s.kind === "books");
  const wz = st?.wazzup;
  const buyers = st ? Object.values(st.buyers).reduce((a, b) => a + b, 0) : 0;

  return (
    <>
      <div className="flex flex-wrap items-start gap-2.5">
        {hideHead ? null : (
          <span className="grid size-9 flex-none place-items-center rounded-btn bg-primary/10 text-primary" aria-hidden>
            <i className="ri-links-line text-lg leading-none" />
          </span>
        )}
        <div className="min-w-0 flex-1">
          {hideHead ? null : <h3 className="text-base font-semibold leading-snug">{t("Zoho CRM и Books → Hub")}</h3>}
          <p className="mt-0.5 text-[12px] text-muted-foreground">
            {st?.configured === false
              ? t("Zoho не подключён: нужны ключи в настройках сервера.")
              : t("Только чтение · автоматически каждые {min} мин · последняя удачная: {when}", { min: st?.everyMinutes ?? "…", when: when(books?.okAt ?? null) })}
            {st?.running ? ` · ${t("идёт синхронизация…")}` : ""}
          </p>
          {books?.error ? <p className="mt-1 text-[12px] text-red-600">{t("Ошибка последнего запуска: {error}", { error: books.error })}</p> : null}
        </div>
        {isAdmin ? (
          <button
            type="button"
            disabled={st?.running || st?.configured === false}
            onClick={() => void z.sync()}
            className="inline-flex h-9 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-btn bg-primary px-3 text-[12.5px] font-semibold text-primary-foreground transition hover:brightness-110 disabled:cursor-default disabled:opacity-50"
          >
            <i className={cn("ri-refresh-line", st?.running && "animate-spin")} aria-hidden />
            {t("Синхронизировать сейчас")}
          </button>
        ) : null}
      </div>

      {st ? (
        <div className="mt-4 grid grid-cols-2 gap-2 lg:grid-cols-4">
          {[
            { label: t("Компаний в Hub"), value: st.companies.toLocaleString(locale), hint: t("клиентов Books: {n}", { n: st.customers.toLocaleString(locale) }) },
            { label: t("Покупатели связаны"), value: t("{a} из {b}", { a: st.buyers.linked, b: buyers }), hint: aed(st.revenue.linked) },
            { label: t("Есть подсказка"), value: String(st.buyers.suggested), hint: aed(st.revenue.suggested), tone: "text-amber-600" },
            { label: t("Без кандидата"), value: String(st.buyers.unlinked), hint: aed(st.revenue.unlinked) },
          ].map((k) => (
            <div key={k.label} className="rounded-btn border border-border bg-background/60 px-3 py-2.5">
              <p className="text-[11px] font-semibold text-muted-foreground">{k.label}</p>
              <p className={cn("mt-1 text-lg font-semibold tabular-nums", k.tone)}>{k.value}</p>
              <p className="text-[11px] text-muted-foreground">{k.hint}</p>
            </div>
          ))}
        </div>
      ) : null}
      {st && st.configured !== false ? (
        <div className="mt-4 flex flex-wrap items-start gap-2.5 rounded-btn border border-border bg-background/60 px-3 py-2.5">
          <span className="grid size-8 flex-none place-items-center rounded-btn bg-primary/10 text-primary" aria-hidden>
            <i className="ri-archive-line text-base leading-none" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-[12.5px] font-semibold">{t("История переписки Wazzup")}</p>
            <p className="mt-0.5 text-[11.5px] leading-relaxed text-muted-foreground">
              {wz && wz.okAt
                ? t("{m} сообщений в {d} диалогах, новых контактов {c} · последний импорт: {when}", {
                    m: wz.messages.toLocaleString(locale),
                    d: wz.conversations.toLocaleString(locale),
                    c: wz.contacts.toLocaleString(locale),
                    when: when(wz.okAt),
                  })
                : t("Журнал Wazzup из Zoho CRM (около 125 тыс. сообщений с июня 2024) ещё не импортирован.")}
              {wz?.running
                ? ` · ${t("идёт импорт…")}${wz.run ? ` ${t("({n} сообщений)", { n: wz.run.messages.toLocaleString(locale) })}` : ""}`
                : ""}
              {" "}
              {t("Диалоги попадают в «Закрытые» во «Входящих» и привязываются к контактам по номеру; после первого импорта новые записи докачиваются вместе с синхронизацией.")}
            </p>
            {wz?.error ? <p className="mt-1 text-[11.5px] text-red-600">{t("Ошибка последнего запуска: {error}", { error: wz.error })}</p> : null}
          </div>
          {isAdmin ? (
            <button
              type="button"
              disabled={wz?.running}
              onClick={() => void z.importWazzup(false)}
              className="inline-flex h-8 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-btn border border-primary/40 px-3 text-[12px] font-semibold text-primary transition hover:bg-primary/10 disabled:cursor-default disabled:opacity-50"
            >
              <i className={cn("ri-download-2-line", wz?.running && "animate-pulse")} aria-hidden />
              {wz?.okAt ? t("Докачать новые") : t("Импортировать историю")}
            </button>
          ) : null}
          {isAdmin && wz?.okAt ? (
            <button
              type="button"
              onClick={() => void z.linkWazzup()}
              title={t("Диалоги архива → сделка CRM → компания и контакт: WhatsApp-переписка попадает в карточку компании")}
              className="inline-flex h-8 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-btn border border-border px-3 text-[12px] font-semibold text-muted-foreground transition hover:border-primary/50 hover:text-primary"
              data-testid="wazzup-link"
            >
              <i className="ri-links-line" aria-hidden />
              {t("Привязать архив к компаниям")}
            </button>
          ) : null}
          {isAdmin && wz?.okAt ? (
            <button
              type="button"
              onClick={() => void z.dedupeContacts()}
              title={t("Контакт CRM без номера и контакт переписки с номером клиента Books — один человек: остаётся контакт CRM, к нему переходят диалоги, сделки и финансы")}
              className="inline-flex h-8 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-btn border border-border px-3 text-[12px] font-semibold text-muted-foreground transition hover:border-primary/50 hover:text-primary"
              data-testid="contacts-dedupe"
            >
              <i className="ri-git-merge-line" aria-hidden />
              {t("Слить дубли по телефону")}
            </button>
          ) : null}
        </div>
      ) : null}
      {sp && sp.configured !== false ? (
        <div className="mt-3 rounded-btn border border-border bg-background/60 px-3 py-2.5">
          <div className="flex flex-wrap items-start gap-2.5">
            <span className="grid size-8 flex-none place-items-center rounded-btn bg-primary/10 text-primary" aria-hidden>
              <i className="ri-user-star-line text-base leading-none" />
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-[12.5px] font-semibold">{t("Продавцы Zoho (Sales Person)")}</p>
              <p className="mt-0.5 text-[11.5px] leading-relaxed text-muted-foreground">
                {t("Диалоги архива Wazzup и клиенты распределяются по полю Sales Person: сделка из журнала Wazzup → контакт CRM → клиент Books. Привяжите продавца к сотруднику Hub — его диалоги без ответственного перейдут к нему вместе с клиентами.")}
                {sp.okAt
                  ? ` ${t("Последний запуск: {when} — продавец определён у {n} диалогов, назначено {m}.", { when: when(sp.okAt), n: sp.stats?.resolved ?? 0, m: sp.stats?.assigned ?? 0 })}`
                  : ""}
                {sp.withoutSalesperson ? ` ${t("Без продавца: {n} диалогов.", { n: sp.withoutSalesperson.toLocaleString(locale) })}` : ""}
                {sp.running ? ` · ${t("идёт распределение…")}` : ""}
              </p>
              {sp.error ? <p className="mt-1 text-[11.5px] text-red-600">{t("Ошибка последнего запуска: {error}", { error: sp.error })}</p> : null}
            </div>
            {isAdmin ? (
              <button
                type="button"
                disabled={sp.running}
                onClick={() => void z.runSalespersons()}
                className="inline-flex h-8 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-btn border border-primary/40 px-3 text-[12px] font-semibold text-primary transition hover:bg-primary/10 disabled:cursor-default disabled:opacity-50"
              >
                <i className={cn("ri-user-shared-line", sp.running && "animate-pulse")} aria-hidden />
                {sp.okAt ? t("Распределить снова") : t("Распределить диалоги")}
              </button>
            ) : null}
          </div>
          {sp.items.length ? (
            <ul className="mt-2 flex flex-col divide-y divide-border/60 rounded-btn border border-border">
              {sp.items.map((row) => (
                <li key={row.name} className="flex flex-wrap items-center gap-x-3 gap-y-1.5 px-3 py-2">
                  <span className="min-w-[10rem] flex-1">
                    <span className="block truncate text-[12.5px] font-semibold">
                      {row.name}
                      {!row.active ? <span className="ms-1.5 rounded-full bg-muted px-1.5 py-0.5 text-[9.5px] font-semibold text-muted-foreground">{t("неактивен")}</span> : null}
                    </span>
                    <span className="block truncate text-[10.5px] text-muted-foreground">
                      {row.email || t("почта не указана")} · {t("сделок: {n}", { n: row.deals.toLocaleString(locale) })} · {t("диалогов: {n}", { n: row.dialogs.toLocaleString(locale) })}
                      {row.unassigned ? ` · ${t("без ответственного: {n}", { n: row.unassigned.toLocaleString(locale) })}` : ""}
                    </span>
                  </span>
                  {isAdmin ? (
                    <span className="flex flex-wrap items-center gap-1.5">
                      <select
                        value={row.userId ?? ""}
                        disabled={spBusy === row.name}
                        onChange={(e) => void z.linkSalesperson(row, { userId: e.target.value || null })}
                        aria-label={t("Сотрудник Hub для {name}", { name: row.name })}
                        className={cn(input, "h-8 w-56 text-[12px]")}
                      >
                        <option value="">{t("— не привязан —")}</option>
                        {sp.users.map((u) => (
                          <option key={u.id} value={u.id}>
                            {u.name} · {u.email}
                          </option>
                        ))}
                      </select>
                      {!row.userId ? (
                        <>
                          <input
                            value={spEmail[row.name] ?? row.email}
                            onChange={(e) => setSpEmail((m) => ({ ...m, [row.name]: e.target.value }))}
                            placeholder={t("почта сотрудника")}
                            aria-label={t("Почта для нового сотрудника {name}", { name: row.name })}
                            className={cn(input, "h-8 w-52 text-[12px]")}
                          />
                          <button
                            type="button"
                            disabled={spBusy === row.name || !(spEmail[row.name] ?? row.email).trim()}
                            onClick={() => void z.linkSalesperson(row, { createUser: { email: (spEmail[row.name] ?? row.email).trim(), role: "agent" } })}
                            className="inline-flex h-8 cursor-pointer items-center whitespace-nowrap rounded-btn border border-border px-2.5 text-[11.5px] font-semibold text-muted-foreground transition hover:text-foreground disabled:opacity-50"
                          >
                            {t("Завести агента")}
                          </button>
                        </>
                      ) : (
                        <span className="text-[11px] text-muted-foreground">{row.user}</span>
                      )}
                    </span>
                  ) : (
                    <span className="text-[11.5px] text-muted-foreground">{row.user ?? t("не привязан")}</span>
                  )}
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}
    </>
  );
}
